// ---------------------------------------------------------------------------
// YouAreYou (uru) — QrShareModal
//
// Modal component that generates a QR code for a trust link URL and provides
// copy-to-clipboard and download-as-PNG actions.
//
// Referenced from the desktop mockup's "One-Click Share" feature card which
// mentions "Instantly share links via QR or URL." This modal is triggered
// from the Dashboard when a user wants to share their trust link via QR code.
//
// Features:
//   - Generates a QR code entirely client-side (canvas-based, no external API)
//   - Copy link to clipboard
//   - Download QR code as PNG image
//   - Share via Web Share API (mobile)
//   - Responsive: centered modal on desktop, bottom sheet on mobile
//   - Accessible: focus trap, Escape to close, aria attributes
//   - Branded: YouAreYou logo colors in QR code, primary blue accent
//
// Usage:
//   <QrShareModal
//     isOpen={showQrModal}
//     onClose={() => setShowQrModal(false)}
//     url="https://verify-uru.com/v/j4Kx9mQ"
//     title="Jane Smith"
//   />
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import Icon from "@/components/ui/Icon";
import BrandName from "@/components/ui/BrandName";
import { copyToClipboard, cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface QrShareModalProps {
  /** Whether the modal is open. */
  isOpen: boolean;

  /** Callback to close the modal. */
  onClose: () => void;

  /** The full URL to encode in the QR code. */
  url: string;

  /**
   * Display title shown above the QR code.
   * Typically the user's name or "Your Trust Link".
   */
  title?: string;

  /**
   * Optional callback invoked on the first copy, download, or share action.
   * Should create the direct link and return its URL. If omitted or if it
   * throws, the modal falls back to the `url` prop.
   */
  onBeforeShare?: () => Promise<string>;
}

// ---------------------------------------------------------------------------
// QR Code Generator
//
// Minimal QR code generator that produces a Module matrix.
// Supports alphanumeric and byte mode encoding at error correction level M.
// This avoids an external dependency for a single feature.
//
// For production use with complex URLs, consider swapping this out for a
// well-tested library like `qrcode` or `qrcode-generator`. This
// implementation handles standard ASCII URLs up to ~200 characters which
// is sufficient for verify-uru.com trust links.
// ---------------------------------------------------------------------------

// We use a canvas-based approach: encode the URL into modules, then draw
// them onto an offscreen canvas. The canvas is displayed in the modal and
// can be exported as a PNG.

/**
 * Generate QR code modules using the Canvas API and a data URL.
 * Falls back to a simple visual placeholder if encoding fails.
 *
 * This uses a "draw text on canvas" approach with a monospace-like
 * encoding as a simplified strategy. For the real QR generation we
 * use the native `qrcode-generator` algorithm embedded below.
 */

// ---------------------------------------------------------------------------
// Tiny QR Code encoder (Mode Byte, EC Level M)
//
// This is a stripped-down QR encoder sufficient for URLs up to ~200 chars.
// Based on the QR code specification (ISO/IEC 18004).
// It produces a boolean[][] grid of modules.
// ---------------------------------------------------------------------------

// Galois Field GF(256) tables
const EXP_TABLE = new Uint8Array(256);
const LOG_TABLE = new Uint8Array(256);

(function initGF() {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP_TABLE[i] = x;
    LOG_TABLE[x] = i;
    x = x << 1;
    if (x & 0x100) x ^= 0x11d;
  }
  EXP_TABLE[255] = EXP_TABLE[0];
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP_TABLE[(LOG_TABLE[a] + LOG_TABLE[b]) % 255];
}

function polyMul(a: number[], b: number[]): number[] {
  const result = new Array(a.length + b.length - 1).fill(0);
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      result[i + j] ^= gfMul(a[i], b[j]);
    }
  }
  return result;
}

function polyMod(dividend: number[], divisor: number[]): number[] {
  const result = [...dividend];
  for (let i = 0; i < dividend.length - divisor.length + 1; i++) {
    if (result[i] === 0) continue;
    const coef = result[i];
    for (let j = 0; j < divisor.length; j++) {
      result[i + j] ^= gfMul(divisor[j], coef);
    }
  }
  return result.slice(dividend.length - divisor.length + 1);
}

function generatorPoly(numEcWords: number): number[] {
  let g = [1];
  for (let i = 0; i < numEcWords; i++) {
    g = polyMul(g, [1, EXP_TABLE[i]]);
  }
  return g;
}

// QR version parameters for byte mode, EC level M
// [version, totalCodewords, ecCodewordsPerBlock, numBlocks, dataCodewords]
const VERSION_TABLE: [number, number, number, number, number][] = [
  [1, 26, 10, 1, 16],
  [2, 44, 16, 1, 28],
  [3, 70, 26, 1, 44],
  [4, 100, 18, 2, 64],
  [5, 134, 24, 2, 86],
  [6, 172, 16, 4, 108],
  [7, 196, 18, 4, 124],
  [8, 242, 22, 4, 154],
  [9, 292, 22, 4, 182],
  [10, 346, 26, 4, 216],
];

function selectVersion(dataLen: number): (typeof VERSION_TABLE)[0] {
  for (const v of VERSION_TABLE) {
    // Byte mode overhead: 4 (mode) + charCountBits + data
    const charCountBits = v[0] <= 9 ? 8 : 16;
    const availableBits = v[4] * 8;
    const neededBits = 4 + charCountBits + dataLen * 8;
    if (neededBits <= availableBits) return v;
  }
  // Fallback to largest we support
  return VERSION_TABLE[VERSION_TABLE.length - 1];
}

function encodeData(data: Uint8Array): {
  version: number;
  modules: boolean[][];
} {
  const vInfo = selectVersion(data.length);
  const [version, , ecPerBlock, numBlocks, totalDataCw] = vInfo;
  const charCountBits = version <= 9 ? 8 : 16;

  // Build bit stream
  const bits: number[] = [];
  const pushBits = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) {
      bits.push((val >> i) & 1);
    }
  };

  // Mode indicator: byte = 0100
  pushBits(0b0100, 4);
  // Character count
  pushBits(data.length, charCountBits);
  // Data
  for (const byte of data) {
    pushBits(byte, 8);
  }
  // Terminator
  const totalDataBits = totalDataCw * 8;
  const terminatorLen = Math.min(4, totalDataBits - bits.length);
  pushBits(0, terminatorLen);

  // Pad to byte boundary
  while (bits.length % 8 !== 0) bits.push(0);

  // Pad codewords
  const padBytes = [0xec, 0x11];
  let padIdx = 0;
  while (bits.length < totalDataBits) {
    pushBits(padBytes[padIdx % 2], 8);
    padIdx++;
  }

  // Convert bits to codewords
  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | (bits[i + j] || 0);
    codewords.push(byte);
  }

  // Error correction
  const dataPerBlock = Math.floor(totalDataCw / numBlocks);
  const extraBlocks = totalDataCw - dataPerBlock * numBlocks;
  const gen = generatorPoly(ecPerBlock);

  const dataBlocks: number[][] = [];
  const ecBlocks: number[][] = [];
  let offset = 0;

  for (let b = 0; b < numBlocks; b++) {
    const blockSize = dataPerBlock + (b >= numBlocks - extraBlocks ? 1 : 0);
    const blockData = codewords.slice(offset, offset + blockSize);
    offset += blockSize;
    dataBlocks.push(blockData);

    const padded = [...blockData, ...new Array(ecPerBlock).fill(0)];
    const ec = polyMod(padded, gen);
    ecBlocks.push(ec);
  }

  // Interleave data blocks
  const interleaved: number[] = [];
  const maxDataLen = Math.max(...dataBlocks.map((b) => b.length));
  for (let i = 0; i < maxDataLen; i++) {
    for (const block of dataBlocks) {
      if (i < block.length) interleaved.push(block[i]);
    }
  }
  for (let i = 0; i < ecPerBlock; i++) {
    for (const block of ecBlocks) {
      if (i < block.length) interleaved.push(block[i]);
    }
  }

  // Build module grid
  const size = version * 4 + 17;
  const modules: (boolean | null)[][] = Array.from({ length: size }, () =>
    new Array(size).fill(null),
  );
  const reserved: boolean[][] = Array.from({ length: size }, () =>
    new Array(size).fill(false),
  );

  // Helper to set a module
  const set = (row: number, col: number, val: boolean, isReserved = false) => {
    if (row >= 0 && row < size && col >= 0 && col < size) {
      modules[row][col] = val;
      if (isReserved) reserved[row][col] = true;
    }
  };

  // Finder patterns
  const drawFinder = (row: number, col: number) => {
    for (let r = -1; r <= 7; r++) {
      for (let c = -1; c <= 7; c++) {
        const inOuter = r >= 0 && r <= 6 && c >= 0 && c <= 6;
        const inInner = r >= 2 && r <= 4 && c >= 2 && c <= 4;
        const onBorder = r === 0 || r === 6 || c === 0 || c === 6;
        const val = inInner || (inOuter && onBorder);
        set(row + r, col + c, val, true);
      }
    }
  };
  drawFinder(0, 0);
  drawFinder(0, size - 7);
  drawFinder(size - 7, 0);

  // Timing patterns
  for (let i = 8; i < size - 8; i++) {
    const val = i % 2 === 0;
    set(6, i, val, true);
    set(i, 6, val, true);
  }

  // Alignment patterns (for version >= 2)
  if (version >= 2) {
    const alignPos = getAlignmentPositions(version);
    for (const r of alignPos) {
      for (const c of alignPos) {
        // Skip if overlapping with finder patterns
        if (r <= 8 && c <= 8) continue;
        if (r <= 8 && c >= size - 8) continue;
        if (r >= size - 8 && c <= 8) continue;
        for (let dr = -2; dr <= 2; dr++) {
          for (let dc = -2; dc <= 2; dc++) {
            const val =
              Math.abs(dr) === 2 ||
              Math.abs(dc) === 2 ||
              (dr === 0 && dc === 0);
            set(r + dr, c + dc, val, true);
          }
        }
      }
    }
  }

  // Dark module
  set(size - 8, 8, true, true);

  // Reserve format info areas
  for (let i = 0; i < 15; i++) {
    // Around top-left finder
    if (i < 6) {
      reserved[8][i] = true;
    } else if (i < 8) {
      reserved[8][i + 1] = true;
    } else if (i < 9) {
      reserved[8 - (i - 8)][8] = true;
    } else {
      reserved[14 - i][8] = true;
    }
    // Around other finders
    if (i < 8) {
      reserved[8][size - 1 - i] = true;
    } else {
      reserved[size - 1 - (14 - i)][8] = true;
    }
  }

  // Reserve version info areas (version >= 7)
  if (version >= 7) {
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 3; j++) {
        reserved[i][size - 11 + j] = true;
        reserved[size - 11 + j][i] = true;
      }
    }
  }

  // Place data bits
  const dataBits: number[] = [];
  for (const byte of interleaved) {
    for (let i = 7; i >= 0; i--) {
      dataBits.push((byte >> i) & 1);
    }
  }

  let bitIdx = 0;
  // Traverse columns from right to left, in pairs
  for (let right = size - 1; right >= 1; right -= 2) {
    // Skip column 6 (timing pattern)
    const col = right <= 6 ? right - 1 : right;

    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const c = col - j;
        if (c < 0 || c >= size) continue;
        const isUpward = ((size - 1 - col) >> 1) & 1;
        const row = isUpward ? size - 1 - vert : vert;

        if (reserved[row][c] || modules[row][c] !== null) continue;

        const bit = bitIdx < dataBits.length ? dataBits[bitIdx] : 0;
        modules[row][c] = bit === 1;
        bitIdx++;
      }
    }
  }

  // Apply mask (we use mask pattern 0: (row + col) % 2 === 0)
  const maskPattern = 0;
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (reserved[r][c]) continue;
      let shouldFlip = false;
      switch (maskPattern) {
        case 0:
          shouldFlip = (r + c) % 2 === 0;
          break;
      }
      if (shouldFlip && modules[r][c] !== null) {
        modules[r][c] = !modules[r][c];
      }
    }
  }

  // Write format info (EC level M = 00, mask 0 = 000)
  // Format info bits for M-0: pre-computed with BCH error correction
  const formatBits = 0b101010000010010; // EC M, mask 0
  for (let i = 0; i < 15; i++) {
    const bit = ((formatBits >> (14 - i)) & 1) === 1;
    // Around top-left
    if (i < 6) {
      modules[8][i] = bit;
    } else if (i < 8) {
      modules[8][i + 1] = bit;
    } else if (i < 9) {
      modules[8 - (i - 8)][8] = bit;
    } else {
      modules[14 - i][8] = bit;
    }
    // Around bottom-left and top-right
    if (i < 8) {
      modules[8][size - 1 - i] = bit;
    } else {
      modules[size - 1 - (14 - i)][8] = bit;
    }
  }

  // Convert null to false
  const result: boolean[][] = modules.map((row) => row.map((m) => m === true));

  return { version, modules: result };
}

function getAlignmentPositions(version: number): number[] {
  if (version === 1) return [];
  const size = version * 4 + 17;
  const step =
    version === 2
      ? size - 7 - 6
      : Math.ceil((size - 13) / (Math.ceil(version / 7) + 1));
  const positions = [6];
  let pos = size - 7;
  while (pos > 6) {
    positions.unshift(pos);
    pos -= step;
  }
  return positions;
}

// ---------------------------------------------------------------------------
// Canvas drawing
// ---------------------------------------------------------------------------

function drawQrToCanvas(
  canvas: HTMLCanvasElement,
  modules: boolean[][],
  opts: {
    moduleSize: number;
    quietZone: number;
    darkColor: string;
    lightColor: string;
  },
) {
  const { moduleSize, quietZone, darkColor, lightColor } = opts;
  const size = modules.length;
  const canvasSize = size * moduleSize + quietZone * 2;

  canvas.width = canvasSize;
  canvas.height = canvasSize;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Background
  ctx.fillStyle = lightColor;
  ctx.fillRect(0, 0, canvasSize, canvasSize);

  // Modules
  ctx.fillStyle = darkColor;
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (modules[r][c]) {
        ctx.fillRect(
          quietZone + c * moduleSize,
          quietZone + r * moduleSize,
          moduleSize,
          moduleSize,
        );
      }
    }
  }
}

// ---------------------------------------------------------------------------
// QrShareModal Component
// ---------------------------------------------------------------------------

export default function QrShareModal({
  isOpen,
  onClose,
  url,
  title = "Your Trust Link",
  onBeforeShare,
}: QrShareModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [qrError, setQrError] = useState(false);
  const [canShare] = useState(() => typeof navigator.share === "function");

  // Tracks the resolved URL — starts as the `url` prop (globalLink), and is
  // replaced with the direct link URL after the first user action.
  const [activeUrl, setActiveUrl] = useState(url);
  const linkCreatedRef = useRef(false);
  const [isCreatingLink, setIsCreatingLink] = useState(false);

  // Reset resolved state each time the modal opens or the base url changes.
  useEffect(() => {
    setActiveUrl(url);
    linkCreatedRef.current = false;
  }, [url, isOpen]);

  // Draw QR code onto the canvas. Called from a ref-callback on the canvas
  // element so it fires exactly when the canvas mounts into the DOM (i.e.
  // when `isOpen` becomes true) and again if `url` changes.
  const drawQr = useCallback(
    (canvas: HTMLCanvasElement | null) => {
      canvasRef.current = canvas;
      if (!canvas) return;

      try {
        const data = new TextEncoder().encode(activeUrl);
        const { modules } = encodeData(data);

        drawQrToCanvas(canvas, modules, {
          moduleSize: 8,
          quietZone: 32,
          darkColor: "#0f172a",
          lightColor: "#ffffff",
        });
        setQrError(false);
      } catch {
        setQrError(true);
      }
    },
    [activeUrl],
  );

  // Lazily creates the direct link on the first user action. Subsequent calls
  // return the already-resolved URL without hitting the API again.
  const resolveUrl = useCallback(async (): Promise<string> => {
    if (linkCreatedRef.current || !onBeforeShare) return activeUrl;
    setIsCreatingLink(true);
    try {
      const newUrl = await onBeforeShare();
      linkCreatedRef.current = true;
      setActiveUrl(newUrl);
      return newUrl;
    } catch {
      return activeUrl;
    } finally {
      setIsCreatingLink(false);
    }
  }, [onBeforeShare, activeUrl]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Close on backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    },
    [onClose],
  );

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // ---- Actions ----

  const handleCopy = useCallback(async () => {
    const urlToUse = await resolveUrl();
    const success = await copyToClipboard(urlToUse);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [resolveUrl]);

  const handleDownload = useCallback(async () => {
    const urlToUse = await resolveUrl();

    try {
      // Draw to a fresh offscreen canvas with the resolved URL so we don't
      // have to wait for React to re-render the main canvas.
      const offscreen = document.createElement("canvas");
      const data = new TextEncoder().encode(urlToUse);
      const { modules } = encodeData(data);
      drawQrToCanvas(offscreen, modules, {
        moduleSize: 8,
        quietZone: 32,
        darkColor: "#0f172a",
        lightColor: "#ffffff",
      });
      const dataUrl = offscreen.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `youareyou-trust-link-qr.png`;
      link.href = dataUrl;
      link.click();
      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 2000);
    } catch {
      // Canvas error — silent fail
    }
  }, [resolveUrl]);

  const handleShare = useCallback(async () => {
    if (!navigator.share) return;

    const urlToUse = await resolveUrl();

    try {
      // Try sharing the image if canvas is available
      if (canvasRef.current) {
        const blob = await new Promise<Blob | null>((resolve) =>
          canvasRef.current!.toBlob(resolve, "image/png"),
        );

        if (blob) {
          const file = new File([blob], "youareyou-trust-link-qr.png", {
            type: "image/png",
          });

          // Check if the device supports file sharing
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: `${title} — YouAreYou Trust Link`,
              text: `Verify my identity: ${urlToUse}`,
              files: [file],
            });
            return;
          }
        }
      }

      // Fallback: share URL only
      await navigator.share({
        title: `${title} — YouAreYou Trust Link`,
        text: `Verify my identity: ${urlToUse}`,
        url: urlToUse,
      });
    } catch (err) {
      // User cancelled or share failed — that's fine
      if (err instanceof Error && err.name !== "AbortError") {
        console.warn("Share failed:", err);
      }
    }
  }, [resolveUrl, title]);

  // ---- Render ----

  if (!isOpen) return null;

  const displayUrl = activeUrl.replace(/^https?:\/\//, "");

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Share trust link QR code"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <div
        ref={modalRef}
        className={cn(
          "relative z-10 w-full max-w-md overflow-hidden bg-white shadow-2xl",
          // Mobile: bottom sheet with rounded top
          "rounded-t-2xl sm:rounded-2xl",
          // Animation (CSS-only)
          "animate-in sm:animate-in",
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-text-primary">
              Share Trust Link
            </h2>
            <p className="mt-0.5 text-xs text-text-muted">
              Scan this QR code to verify identity
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-text-muted transition-colors hover:bg-gray-100 hover:text-text-primary"
            aria-label="Close"
          >
            <Icon name="close" className="text-xl" />
          </button>
        </div>

        {/* QR Code */}
        <div className="flex flex-col items-center px-5 py-6">
          {/* Title pill */}
          <div className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1">
            <Icon
              name="verified_user"
              filled
              className="text-sm text-primary-500"
            />
            <span className="text-xs font-semibold text-primary-700">
              {title}
            </span>
          </div>

          {/* Canvas */}
          {qrError ? (
            <div className="flex h-64 w-64 flex-col items-center justify-center rounded-xl bg-gray-50">
              <Icon name="error" className="mb-2 text-3xl text-text-muted" />
              <p className="text-sm text-text-muted">
                Could not generate QR code
              </p>
              <p className="mt-1 text-xs text-text-muted">
                Use the link below instead
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-gray-100 bg-white p-2 shadow-sm">
              <canvas
                ref={drawQr}
                className="h-64 w-64"
                style={{ imageRendering: "pixelated" }}
                aria-label={`QR code for ${displayUrl}`}
                role="img"
              />
            </div>
          )}

          {/* URL display */}
          <div className="mt-4 flex w-full items-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
            <Icon name="link" className="shrink-0 text-lg text-text-muted" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
              {isCreatingLink ? "Creating link…" : displayUrl}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-2 border-t border-gray-100 px-5 py-4">
          {/* Primary row: Copy + Download (+ Share on mobile) */}
          <div className="flex gap-2">
            {/* Copy button */}
            <button
              onClick={handleCopy}
              disabled={isCreatingLink}
              className={cn(
                "flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors focus:ring-2 focus:ring-primary-500/20 focus:outline-none",
                copied
                  ? "bg-green-50 text-verified"
                  : "bg-primary-500 text-white hover:bg-primary-600",
                isCreatingLink && "cursor-wait opacity-70",
              )}
            >
              <Icon
                name={copied ? "check" : "content_copy"}
                className="text-lg"
              />
              {copied ? "Copied!" : "Copy Link"}
            </button>

            {/* Download button */}
            <button
              onClick={handleDownload}
              disabled={qrError || isCreatingLink}
              className={cn(
                "flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors focus:ring-2 focus:ring-primary-500/20 focus:outline-none",
                downloaded
                  ? "border-green-200 bg-green-50 text-verified"
                  : "border-gray-200 bg-white text-text-primary hover:bg-gray-50",
                (qrError || isCreatingLink) && "cursor-not-allowed opacity-50",
              )}
            >
              <Icon
                name={downloaded ? "check" : "download"}
                className="text-lg"
              />
              {downloaded ? "Saved!" : "Download"}
            </button>
          </div>

          {/* Share button (mobile only, uses Web Share API) */}
          {canShare && (
            <button
              onClick={handleShare}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-text-primary transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
            >
              <Icon name="share" className="text-lg text-primary-500" />
              Share via…
            </button>
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-gray-100 px-5 py-3 text-center">
          <p className="text-[11px] text-text-muted">
            <Icon
              name="lock"
              className="mr-0.5 inline-block align-text-bottom text-xs"
            />
            Secure, Encrypted Identity Verification by{" "}
            <BrandName />
          </p>
        </div>
      </div>
    </div>
  );
}
